import React, { useState, useMemo, useRef, useEffect } from 'react'
import { Container, Row, Col, Form, Card } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getHelpContent } from '../data/helpArticles/index.js'

/**
 * Documentation — in-platform Help Center for community managers.
 *
 * Left: searchable list of articles grouped by category.
 * Right: the selected article. The active article is kept in the URL
 * (?a=<id>) so links can point straight to a specific topic.
 *
 * Translated content per language lives in data/helpArticles/{lang}/.
 * English (the source of truth) lives in data/helpArticles.jsx.
 */
function Documentation() {
  const [params, setParams] = useSearchParams()
  const [query, setQuery] = useState('')
  const contentRef = useRef(null)
  const { t, i18n } = useTranslation()

  const { HELP_CATEGORIES, HELP_ARTICLES } = useMemo(
    () => getHelpContent(i18n.language),
    [i18n.language]
  )

  const activeId = params.get('a') || HELP_ARTICLES[0].id
  const article = HELP_ARTICLES.find((a) => a.id === activeId) || HELP_ARTICLES[0]

  // Filter articles by the search box (matches title + summary).
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return HELP_ARTICLES
    return HELP_ARTICLES.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        a.summary.toLowerCase().includes(q)
    )
  }, [query])

  const selectArticle = (id) => setParams({ a: id })

  // Scroll the article into view whenever it changes — covers both sidebar
  // clicks and in-article cross-links. Skipped on first render so the page
  // does not auto-scroll past its header on load.
  const didMount = useRef(false)
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true
      return
    }
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [activeId])

  return (
    <Container style={{ maxWidth: '1300px' }} className="py-2">
      {/* Page header */}
      <div className="mb-4">
        <h1 style={{ color: 'var(--color-primary-dark)', fontSize: 'var(--font-size-2xl)' }}>
          {t('documentation_page.title')}
        </h1>
        <p className="text-secondary" style={{ marginBottom: 0 }}>
          {t('documentation_page.subtitle')}
        </p>
      </div>

      <Row className="g-4">
        {/* Sidebar */}
        <Col lg={3} md={4}>
          <div style={{ position: 'sticky', top: '1rem' }}>
            <Form.Control
              type="search"
              placeholder={t('documentation_page.search_placeholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="mb-3"
            />
            {HELP_CATEGORIES.map((cat) => {
              const arts = visible.filter((a) => a.category === cat.id)
              if (arts.length === 0) return null
              return (
                <div key={cat.id} className="mb-3">
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: 'var(--color-neutral-500)',
                      padding: '4px 8px',
                    }}
                  >
                    <span className="me-1">{cat.icon}</span>{cat.label}
                  </div>
                  <ul className="list-unstyled mb-0">
                    {arts.map((a) => {
                      const isActive = a.id === activeId
                      return (
                        <li key={a.id}>
                          <button
                            type="button"
                            onClick={() => selectArticle(a.id)}
                            style={{
                              display: 'block',
                              width: '100%',
                              textAlign: 'left',
                              border: 'none',
                              borderRadius: 'var(--radius-md)',
                              padding: '7px 10px',
                              fontSize: 'var(--font-size-sm)',
                              cursor: 'pointer',
                              backgroundColor: isActive ? 'var(--color-primary)' : 'transparent',
                              color: isActive ? '#fff' : 'var(--color-neutral-800)',
                              fontWeight: isActive ? 600 : 400,
                            }}
                          >
                            {a.title}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )
            })}
            {visible.length === 0 && (
              <div className="text-muted small px-2">{t('documentation_page.no_results', { query })}</div>
            )}
          </div>
        </Col>

        {/* Article content */}
        <Col lg={9} md={8}>
          <Card style={{ border: '1px solid var(--color-neutral-200)', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body className="p-4" ref={contentRef}>
              <h2 style={{ color: 'var(--color-primary-dark)', fontSize: 'var(--font-size-xl)' }}>
                {article.title}
              </h2>
              <p
                className="text-secondary"
                style={{ fontSize: 'var(--font-size-sm)', borderBottom: '1px solid var(--color-neutral-200)', paddingBottom: 'var(--space-3)' }}
              >
                {article.summary}
              </p>
              <div className="help-article-body" style={{ lineHeight: 1.7, color: 'var(--color-neutral-800)' }}>
                {article.body}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default Documentation
